import React from "react";
import { X, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useCart } from "../context/CartContext";

const CartDrawer = ({ onCheckout }) => {
    const { t, formatPrice } = useI18n();
    const { items, updateQty, remove, subtotal, open, setOpen } = useCart();

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={() => setOpen(false)}
            />
            {/* Drawer */}
            <aside
                data-testid="cart-drawer"
                className={`fixed inset-y-0 right-0 z-[95] w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <header className="px-6 py-5 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-2xl font-medium tracking-tight">
                            {t("cart_title")}
                        </h2>
                        <p className="text-sm text-neutral-500 mt-0.5">
                            {items.reduce((s, p) => s + p.qty, 0)} {t("filter_all").toLowerCase()}
                        </p>
                    </div>
                    <button
                        data-testid="cart-close-btn"
                        onClick={() => setOpen(false)}
                        className="h-10 w-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center"
                        aria-label="Close cart"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                        <div className="h-20 w-20 rounded-full bg-neutral-100 flex items-center justify-center">
                            <ShoppingBag className="h-9 w-9 text-neutral-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-display text-xl mt-5 font-medium">
                            {t("cart_empty")}
                        </h3>
                        <p className="text-neutral-500 mt-2">{t("cart_empty_hint")}</p>
                        <button
                            data-testid="cart-continue-shopping-btn"
                            onClick={() => {
                                setOpen(false);
                                window.dispatchEvent(new CustomEvent("bazaar:home"));
                            }}
                            className="mt-7 h-12 px-7 rounded-full bg-[#1a1a1a] hover:bg-[#FF5722] text-white font-semibold transition-all"
                        >
                            {t("cart_continue")}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                            {items.map((p) => {
                                const linePrice = formatPrice(p.price_all * p.qty);
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`cart-item-${p.id}`}
                                        className="flex gap-3 group"
                                    >
                                        <div className="h-20 w-20 rounded-xl overflow-hidden bg-neutral-100 shrink-0">
                                            {p.image && (
                                                <img
                                                    src={p.image}
                                                    alt={p.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-semibold text-neutral-900 line-clamp-2">
                                                {p.name}
                                            </h4>
                                            {p.weight && (
                                                <p className="text-xs text-neutral-500 mt-0.5">{p.weight}</p>
                                            )}
                                            <div className="mt-2 flex items-center justify-between">
                                                <div className="inline-flex items-center gap-2 bg-neutral-100 rounded-full px-1 py-1">
                                                    <button
                                                        data-testid={`cart-dec-${p.id}`}
                                                        onClick={() => updateQty(p.id, p.qty - 1)}
                                                        className="h-7 w-7 rounded-full bg-white hover:bg-[#FF5722] hover:text-white flex items-center justify-center transition-colors"
                                                        aria-label="Decrease"
                                                    >
                                                        <Minus className="h-3.5 w-3.5" />
                                                    </button>
                                                    <span className="text-sm font-semibold w-5 text-center">
                                                        {p.qty}
                                                    </span>
                                                    <button
                                                        data-testid={`cart-inc-${p.id}`}
                                                        onClick={() => updateQty(p.id, p.qty + 1)}
                                                        className="h-7 w-7 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white flex items-center justify-center transition-colors shadow-sm"
                                                        aria-label="Increase"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                <span className="font-display text-base font-semibold">
                                                    {linePrice.primary}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <footer className="border-t border-neutral-200 p-6 space-y-4 bg-neutral-50">
                            <div className="flex items-baseline justify-between">
                                <span className="text-sm text-neutral-600">
                                    {t("cart_subtotal")}
                                </span>
                                <div className="text-right">
                                    <div
                                        className="font-display text-2xl font-semibold"
                                        data-testid="cart-subtotal"
                                    >
                                        {formatPrice(subtotal).primary}
                                    </div>
                                    <div className="text-xs text-neutral-400">
                                        {formatPrice(subtotal).secondary}
                                    </div>
                                </div>
                            </div>
                            <button
                                data-testid="cart-checkout-btn"
                                onClick={onCheckout}
                                className="w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-[#FF5722]/30"
                            >
                                {t("cart_checkout")} <ArrowRight className="h-5 w-5" />
                            </button>
                        </footer>
                    </>
                )}
            </aside>
        </>
    );
};

export default CartDrawer;
